import cloneDeep from 'lodash.clonedeep'
import { Fragment, FunctionComponent, useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Container from 'components/GlobalStyles/Marketing/Container'
import TopQuestionArrow from 'components/GlobalStyles/TopQuestionArrow'
import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'

import { prepopulateIntercomMessage } from 'services/intercom'

interface IFAQ {
  question: string
  answer: string
  open: boolean
}
const sampleFAQs: IFAQ[] = [
  {
    question: 'Why choose HomeLister?',
    answer: `We started HomeLister as an easy, stress-free way to get your house on the market, on the MLS, and sold without all the hefty, seller’s agent commission. Our goal is to empower sellers by providing them with the professional tools and unrivaled customer service they need to quickly and easily sell their homes and save a lot of money along the way. We offer our sellers as much - or as little - support as they want. You only pay for what you need - and nothing you don't. So if you need offer and counter offer support, we can do that! If you want a Comparative Market Analysis, you can add that service as well. We offer a full menu of additional services that include everything from professional photography and 3D Matterport tours to appraisal comps, home price reports, and email marketing to local agents. And most importantly, by working with HomeLister, we get your house on the MLS, which means more exposure and more potential buyers for your home.
    HomeLister's easy online dashboard lets you create, manage and edit your listing with just the touch of a button from your phone or computer. And we're available for any and all questions every step of the way.`,
    open: false,
  },
  {
    question: 'Why does being on the MLS matter?',
    answer:
      "When you put your house on the MLS, it will automatically appear on major real estate search sites like Zillow, Redfin, Trulia, etc., which means you’re able to reach buyers from all over the country. More potential buyers mean more potential offers, which will lead to the highest possible sales price. And, of course, that means more money in your pocket! And who doesn't want that?",
    open: false,
  },
  {
    question: 'What if I need help?',
    answer:
      "We're here to help every step of the way. Questions? Concerns? Curious as to how something works? Just reach out. Our team is standing by to help 7 days a week, 1pm-8pm (Eastern) on Mondays, 11am-8pm (Eastern) Tuesday through Friday, and 11am-3pm (Eastern) on Saturday. You can call us at (855) 400-8566 or chat with us by clicking here. After hours? Drop us an email at info@homelister.com and we'll get back to you as quickly as possible. The HomeLister team is here to make your home sale as seamless and easy as possible so don't hesitate to reach out if there's anything we can do to help you.",
    open: false,
  },
]

const Header = styled.h3`
  font-size: 36px;
  font-weight: 800;
  text-align: center;
  margin: 0;
  padding: 0;
  color: ${props => props.theme.colors.grey27};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 44px;
  }
`

const SubHeader = styled.div`
  font-size: 18px;
  font-weight: 500px;
  text-align: center;
  color: ${props => props.theme.colors.grey23};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 20px;
  }
`

const CollapseContainer = styled.div`
  display: flex;
  flex-direction: column;
`

interface IPanelProps {
  open: boolean
}
const Panel = styled.div<IPanelProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 25px;
  padding: 0 32px;
  background: ${props =>
    props.open ? props.theme.colors.transparentYellow4Alpha10 : props.theme.colors.white};
  border: 2px solid
    ${props =>
    props.open ? props.theme.colors.white : props.theme.colors.transparentYellow4Alpha25};

  &:hover {
    background: ${props => props.theme.colors.transparentYellow4Alpha10};
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0 48px;
  }
`

const PanelHeader = styled.div<IPanelProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 20px;
  font-weight: 900;
  height: 80px;
  cursor: pointer;
  color: ${props => props.theme.colors.grey27};
`

const PanelContent = styled.div`
  font-size: 16px;
  line-height: 25px;
  padding-bottom: 24px;
  color: ${props => props.theme.colors.grey27};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin-right: 50px;
  }
`

const EmailLink = styled.a`
  color: ${props => props.theme.colors.blue};
  text-decoration: underline;
  cursor: pointer;
`

const ClickableText = styled.span`
  color: ${props => props.theme.colors.blue};
  text-decoration: underline;
  cursor: pointer;
`

interface IProps {}

const FAQ: FunctionComponent<IProps> = () => {
  const theme = useContext(ThemeContext)

  const [faqs, setFaqs] = useState<IFAQ[]>(sampleFAQs || [])

  const openIntercom = () => prepopulateIntercomMessage('I have questions about...')

  const toggleCollapse = (key: number) => () => {
    setFaqs(current => {
      const updated = cloneDeep(current)
      updated[key].open = !current[key].open
      return updated
    })
  }

  return (
    <Container>
      <Spacer
        size={72}
        tabletSize={48}
        responsiveSize={32}
      />
      <Header>Have questions? We&apos;ve got answers!</Header>
      <Spacer size={32} />
      <SubHeader>FAQs</SubHeader>
      <Spacer
        size={50}
        tabletSize={40}
        responsiveSize={30}
      />
      <CollapseContainer>
        {faqs.map((faq, index) => (
          <Fragment key={faq.question}>
            {index > 0 && (
              <Spacer
                size={24}
                tabletSize={16}
                responsiveSize={16}
              />
            )}
            <Panel
              open={faq.open}
              key={faq.question}
            >
              <PanelHeader
                open={faq.open}
                onClick={toggleCollapse(index)}
              >
                {faq.question}
                <TopQuestionArrow
                  rotated={faq.open}
                  fill={theme.colors.white}
                  strokecap="rounded"
                />
              </PanelHeader>
              {faq.open && (
                <PanelContent>
                  {index === 2
                    ? (
                      <>
                      We&apos;re here to help every step of the way. Questions? Concerns? Curious as
                      to how something works? Just reach out. Our team is standing by to help 7 days
                      a week, 1pm-8pm Eastern on Mondays, 11am-8pm Eastern Tuesday through Friday,
                      and 11am-3pm Eastern on Saturday. You can
                      call us at (855) 400-8566 or chat with us by clicking{' '}
                        <ClickableText onClick={openIntercom}>here</ClickableText>. After hours? Drop
                      us an email at{' '}
                        <EmailLink href="mailto:info@homelister.com">info@homelister.com</EmailLink>{' '}
                      and we&apos;ll get back to you as quickly as possible. The HomeLister team is
                      here to make your home sale as seamless and easy as possible so don&apos;t
                      hesitate to reach out if there&apos;s anything we can do to help you.
                      </>
                    )
                    : (
                      faq.answer
                    )}
                </PanelContent>
              )}
            </Panel>
          </Fragment>
        ))}
      </CollapseContainer>
      <Spacer
        size={50}
        tabletSize={40}
        responsiveSize={30}
      />
      <RoundedButton
        text="SEE MORE FAQs"
        href="/faq"
        background={theme.colors.blue4}
        hoverBackground={theme.colors.blue9}
        color={theme.colors.white}
        boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
        fontSize={12}
        radius={50}
        height={44}
        width={240}
        noUppercase
        center
      />
      <Spacer
        size={72}
        tabletSize={48}
        responsiveSize={32}
      />
    </Container>
  )
}

export default FAQ
